import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Baby, Users as UsersIcon, CheckCircle, XCircle, Eye, FileText, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ProfileForm from './ProfileForm';
import CnsPyramid from './CnsPyramid';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const statusBadge = (status) => {
  const map = {
    pending: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    acknowledged: 'bg-blue-100 text-blue-700 border-blue-300',
    approved: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    rejected: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    cancelled: 'bg-slate-100 text-slate-700 border-slate-300',
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
  };
  return <Badge className={`${map[status] || 'bg-slate-100 text-slate-700 border-slate-300'} border`}>{status}</Badge>;
};

const AdminStudentsSection = () => {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [studentLeaves, setStudentLeaves] = useState([]);
  const [reports, setReports] = useState([]);
  const [otReports, setOtReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(null); // student being assigned
  const [studentDetail, setStudentDetail] = useState(null);
  const [studentAttendance, setStudentAttendance] = useState([]);
  const [attendanceMonth, setAttendanceMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [isEditingStudent, setIsEditingStudent] = useState(false);
  const [editableStudent, setEditableStudent] = useState(null);
  const [parentProfileOpen, setParentProfileOpen] = useState(false);
  const [parentProfileUserId, setParentProfileUserId] = useState(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [attendanceDays, setAttendanceDays] = useState('');
  const [packageName, setPackageName] = useState('');
  const [reportView, setReportView] = useState(null);
  const [otReportView, setOtReportView] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    const fetchSummary = async () => {
      if (!reportView) {
        setAttendanceSummary(null);
        return;
      }
      try {
        const parseMonth = (m) => {
          if (!m) return {};
          const iso = /^\d{4}-\d{2}/.exec(m);
          if (iso) {
            const parts = iso[0].split('-');
            return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) };
          }
          const d = new Date('1 ' + m);
          if (!isNaN(d.getTime())) return { year: d.getFullYear(), month: d.getMonth() + 1 };
          return {};
        };
        const ym = parseMonth(reportView.month) || {};
        const q = [];
        if (reportView.teacher_id) q.push(`user_id=${reportView.teacher_id}`);
        if (ym.year) q.push(`year=${ym.year}`);
        if (ym.month) q.push(`month=${ym.month}`);
        const url = `${API}/api/attendance/monthly${q.length ? '?' + q.join('&') : ''}`;
        const res = await axios.get(url, { withCredentials: true });
        if (mounted) setAttendanceSummary(res.data);
      } catch (err) {
        // ignore
      }
    };
    fetchSummary();
    return () => { mounted = false; };
  }, [reportView]);

  useEffect(() => {
    let mounted = true;
    const fetchAttendance = async () => {
      if (!studentDetail) {
        setStudentAttendance([]);
        return;
      }
      setAttendanceLoading(true);
      try {
        const params = { student_id: studentDetail.id };
        if (attendanceMonth) params.month = attendanceMonth;
        const res = await axios.get(`${API}/api/attendance`, { params, withCredentials: true });
        if (mounted) setStudentAttendance(res.data || []);
      } catch (e) {
        if (mounted) setStudentAttendance([]);
      } finally {
        if (mounted) setAttendanceLoading(false);
      }
    };
    fetchAttendance();
    return () => { mounted = false; };
  }, [studentDetail, attendanceMonth]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, t, l, r, ot] = await Promise.all([
        axios.get(`${API}/api/students`, { withCredentials: true }),
        axios.get(`${API}/api/teachers-list`, { withCredentials: true }),
        axios.get(`${API}/api/student-leaves`, { withCredentials: true }),
        axios.get(`${API}/api/reports`, { withCredentials: true }),
        axios.get(`${API}/api/ot-reports`, { withCredentials: true }),
      ]);
      setStudents(s.data);
      setTeachers(t.data);
      setStudentLeaves(l.data);
      setReports(r.data);
      setOtReports(ot.data.map((item) => ({ ...item, type: 'ot' })));
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const openAssign = (student) => {
    setAssignDialog(student);
    setSelectedTeacherIds(student.teacher_ids || []);
    setAttendanceDays(student.monthly_attendance_days ?? '');
    setPackageName(student.package_name || '');
  };

  const formatMalaysiaTimeOnly = (value) => {
    if (!value) return '—';
    if (/^\d{2}:\d{2}(?::\d{2})?$/.test(value)) return value;
    try {
      return new Date(value).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' });
    } catch (e) {
      return value;
    }
  };

  const saveAssignment = async () => {
    try {
      await axios.put(`${API}/api/students/${assignDialog.id}`, {
        teacher_ids: selectedTeacherIds,
        monthly_attendance_days: attendanceDays === '' ? null : parseInt(attendanceDays, 10),
        package_name: packageName,
      }, { withCredentials: true });
      toast.success('Student updated');
      setAssignDialog(null);
      fetchAll();
    } catch (e) { toast.error('Failed to save'); }
  };

  const toggleTeacher = (id) => {
    setSelectedTeacherIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const updateStudentLeave = async (id, status) => {
    try {
      await axios.patch(`${API}/api/student-leaves/${id}`, { status }, { withCredentials: true });
      toast.success(`Leave ${status}`);
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const approveReport = async (id, status) => {
    try {
      const report = [...reports, ...otReports].find((item) => item.id === id);
      const endpoint = report?.type === 'ot' ? `${API}/api/ot-reports/${id}` : `${API}/api/reports/${id}`;
      await axios.put(endpoint, { status }, { withCredentials: true });
      toast.success(`${report?.type === 'ot' ? 'OT report' : 'Report'} ${status}`);
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteReport = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      const report = [...reports, ...otReports].find((item) => item.id === id);
      const endpoint = report?.type === 'ot' ? `${API}/api/ot-reports/${id}` : `${API}/api/reports/${id}`;
      await axios.delete(endpoint, { withCredentials: true });
      toast.success(`${report?.type === 'ot' ? 'OT report' : 'Report'} deleted`);
      fetchAll();
    } catch (e) {
      // fallback if report type is not determined
      try {
        await axios.delete(`${API}/api/reports/${id}`, { withCredentials: true });
        toast.success('Report deleted');
      } catch (e) {
        try {
          await axios.delete(`${API}/api/ot-reports/${id}`, { withCredentials: true });
          toast.success('OT report deleted');
        } catch (e) {
          toast.error('Failed to delete report');
        }
      }
      fetchAll();
    }
  };

  const deleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`Delete student "${studentName}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/students/${studentId}`, { withCredentials: true });
      toast.success('Student deleted');
      fetchAll();
    } catch (e) { toast.error('Failed to delete student'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  const pendingReports = [...reports, ...otReports].filter((r) => r.status === 'pending');
  const approvedReports = [...reports, ...otReports].filter((r) => r.status === 'approved');

  return (
    <div className="space-y-6" data-testid="admin-students-section">
      {/* Students List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>All Students</CardTitle>
          <CardDescription>View all students, assign teachers, set package (attendance days/month)</CardDescription>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <div className="text-center py-8 text-slate-500"><Baby className="w-12 h-12 mx-auto mb-3 text-slate-400" /><p>No students registered yet</p></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Parent</TableHead><TableHead>Package</TableHead><TableHead>Days/Month</TableHead><TableHead>Assigned Teachers</TableHead><TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-students-table">
                {students.map((s, idx) => (
                  <TableRow key={s.id} data-testid={`admin-student-row-${idx}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{s.parents_name || '—'}</TableCell>
                    <TableCell className="text-sm">{s.package_name || '—'}</TableCell>
                    <TableCell>{s.monthly_attendance_days || 20}</TableCell>
                    <TableCell className="text-sm">
                      {(s.teacher_ids || []).length === 0 ? <span className="text-slate-400">Unassigned</span> :
                        (s.teacher_ids || []).map((tid) => teachers.find(t => t.id === tid)?.name).filter(Boolean).join(', ')}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setStudentDetail(s)} data-testid={`view-student-${idx}`}>
                          <Eye className="w-4 h-4 mr-1" />View
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openAssign(s)} data-testid={`assign-student-${idx}`}>
                          <UsersIcon className="w-4 h-4 mr-1" />Manage
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteStudent(s.id, s.name)} data-testid={`delete-student-${idx}`}>
                          <Trash2 className="w-4 h-4 mr-1" />Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pending Report Approvals */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Report Approvals</CardTitle>
          <CardDescription>Approve/reject monthly reports before parents see them</CardDescription>
        </CardHeader>
        <CardContent>
          {pendingReports.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">No pending reports</div>
          ) : (
            <div className="space-y-3" data-testid="admin-reports-list">
              {pendingReports.map((r, idx) => (
                <div key={r.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`admin-report-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-slate-900">{r.student_name} — {r.type === 'ot' ? 'OT Report' : r.month}</h4>
                      <p className="text-xs text-slate-500">By {r.teacher_name} • {r.type === 'ot' ? '3-month OT progress report' : `Rating: ${r.overall_rating || '—'}/5`}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setReportView(r)} data-testid={`view-report-${idx}`}><Eye className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(`${API}/${r.type === 'ot' ? 'api/ot-reports' : 'api/reports'}/${r.id}${r.type === 'ot' ? '/pdf' : '/pdf'}`, '_blank')} data-testid={`admin-pending-download-${idx}`}><Download className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteReport(r.id)} data-testid={`admin-pending-delete-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                      <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => approveReport(r.id, 'approved')} data-testid={`approve-report-${idx}`}><CheckCircle className="w-4 h-4" /></Button>
                      <Button size="sm" className="bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={() => approveReport(r.id, 'rejected')} data-testid={`reject-report-${idx}`}><XCircle className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approved Reports */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Approved Reports</CardTitle>
          <CardDescription>Download approved monthly reports as PDF</CardDescription>
        </CardHeader>
        <CardContent>
          {approvedReports.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">No approved reports available</div>
          ) : (
            <div className="space-y-3" data-testid="admin-approved-reports-list">
              {approvedReports.map((r, idx) => (
                <div key={r.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`admin-approved-report-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-900">{r.student_name} — {r.type === 'ot' ? 'OT Report' : r.month}</h4>
                      <p className="text-xs text-slate-500">By {r.teacher_name} • {r.type === 'ot' ? 'OT progress report' : `Rating: ${r.overall_rating || '—'}/5`}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => window.open(`${API}/${r.type === 'ot' ? 'api/ot-reports' : 'api/reports'}/${r.id}${r.type === 'ot' ? '/pdf' : '/pdf'}`, '_blank')} data-testid={`admin-approved-download-${idx}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteReport(r.id)} data-testid={`admin-approved-delete-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Student Leaves */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Student Leave Requests</CardTitle>
          <CardDescription>Leaves submitted by parents for their children</CardDescription>
        </CardHeader>
        <CardContent>
          {studentLeaves.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">No leave requests</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Student</TableHead><TableHead>Parent</TableHead><TableHead>Dates</TableHead>
                <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody data-testid="admin-student-leaves-table">
                {studentLeaves.map((l, idx) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{l.student_name}</TableCell>
                    <TableCell className="text-sm">{l.parent_name}</TableCell>
                    <TableCell className="text-sm">{l.start_date} to {l.end_date}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">{l.reason}</TableCell>
                    <TableCell>{statusBadge(l.status)}</TableCell>
                    <TableCell>
                      <select
                        className="text-xs rounded-md border border-slate-300 h-8 px-2"
                        value={l.status}
                        onChange={(e) => updateStudentLeave(l.id, e.target.value)}
                        data-testid={`student-leave-status-${idx}`}
                      >
                        <option value="acknowledged">Acknowledged</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Manage Student: {assignDialog?.name}</DialogTitle>
            <DialogDescription>Assign teachers and set attendance package</DialogDescription>
          </DialogHeader>
            <div className="space-y-4">
              {/* Student Details (read-only) - hidden for admin users */}
              {user && user.role === 'admin' ? (
                <div className="p-3 rounded border border-slate-100 bg-slate-50 text-sm text-slate-600">Student and parent details are hidden for admin users.</div>
              ) : (
                <div>
                  <p className="text-sm font-semibold text-slate-700">Student Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-slate-500">ID Number</p>
                      <p className="text-sm text-slate-700">{assignDialog?.id_number || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Date of Birth</p>
                      <p className="text-sm text-slate-700">{assignDialog?.date_of_birth || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Parent / Guardian</p>
                      <p className="text-sm text-slate-700">{assignDialog?.parents_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Parent Phone</p>
                      <p className="text-sm text-slate-700">{assignDialog?.parents_phone || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Emergency Contact</p>
                      <p className="text-sm text-slate-700">{assignDialog?.emergency_contact_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Emergency Phone</p>
                      <p className="text-sm text-slate-700">{assignDialog?.emergency_contact_phone || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Vehicle Registration</p>
                      <p className="text-sm text-slate-700">{assignDialog?.vehicle_registration || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Vehicle Brand</p>
                      <p className="text-sm text-slate-700">{assignDialog?.vehicle_brand || '—'}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Package Name</Label>
                <Input value={packageName} onChange={(e) => setPackageName(e.target.value)} placeholder="e.g. Full-time" data-testid="package-name-input" />
              </div>
              <div>
                <Label>Attendance Days / Month</Label>
                <Input type="number" min="1" max="31" value={attendanceDays} onChange={(e) => setAttendanceDays(e.target.value)} data-testid="attendance-days-input" />
              </div>
            </div>
            <div>
              <Label>Assign Teachers</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-md p-3" data-testid="teachers-checkbox-list">
                {teachers.length === 0 ? (
                  <p className="text-sm text-slate-500">No teachers available</p>
                ) : (
                  teachers.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-50 rounded" data-testid={`teacher-check-${t.id}`}>
                      <input type="checkbox" checked={selectedTeacherIds.includes(t.id)} onChange={() => toggleTeacher(t.id)} />
                      <div>
                        <span className="text-sm font-medium">{t.name}</span>
                        {t.department && <span className="text-xs text-slate-500 ml-2">({t.department})</span>}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveAssignment} data-testid="save-assignment-button">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!studentDetail} onOpenChange={(o) => !o && setStudentDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Student Details — {studentDetail?.name}</DialogTitle>
            <DialogDescription>View the full student record, emergency contact, vehicle, and ID details</DialogDescription>
          </DialogHeader>
          {studentDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Student Name</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.name}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.name || ''} onChange={(e) => setEditableStudent({ ...editableStudent, name: e.target.value })} data-testid="edit-student-name" />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Parent Name</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.parents_name || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.parents_name || ''} onChange={(e) => setEditableStudent({ ...editableStudent, parents_name: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Parent Phone</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.parents_phone || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.parents_phone || ''} onChange={(e) => setEditableStudent({ ...editableStudent, parents_phone: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Parent Birthday</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.parents_date_of_birth || '—'}</p>
                  ) : (
                    <input type="date" className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.parents_date_of_birth || ''} onChange={(e) => setEditableStudent({ ...editableStudent, parents_date_of_birth: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">ID Number</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.id_number || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.id_number || ''} onChange={(e) => setEditableStudent({ ...editableStudent, id_number: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Date of Birth</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.date_of_birth || '—'}</p>
                  ) : (
                    <input type="date" className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.date_of_birth || ''} onChange={(e) => setEditableStudent({ ...editableStudent, date_of_birth: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Address</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.address || '—'}</p>
                  ) : (
                    <textarea className="w-full border border-slate-200 rounded px-2 py-1" rows={2} value={editableStudent?.address || ''} onChange={(e) => setEditableStudent({ ...editableStudent, address: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Location</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.location || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.location || ''} onChange={(e) => setEditableStudent({ ...editableStudent, location: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Emergency Contact</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.emergency_contact_name || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.emergency_contact_name || ''} onChange={(e) => setEditableStudent({ ...editableStudent, emergency_contact_name: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Emergency Phone</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.emergency_contact_phone || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.emergency_contact_phone || ''} onChange={(e) => setEditableStudent({ ...editableStudent, emergency_contact_phone: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Vehicle Registration</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.vehicle_registration || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.vehicle_registration || ''} onChange={(e) => setEditableStudent({ ...editableStudent, vehicle_registration: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Vehicle Brand</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700">{studentDetail.vehicle_brand || '—'}</p>
                  ) : (
                    <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.vehicle_brand || ''} onChange={(e) => setEditableStudent({ ...editableStudent, vehicle_brand: e.target.value })} />
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Special Needs Notes</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700 whitespace-pre-line">{studentDetail.special_needs_notes || '—'}</p>
                  ) : (
                    <textarea className="w-full border border-slate-200 rounded px-2 py-1" rows={3} value={editableStudent?.special_needs_notes || ''} onChange={(e) => setEditableStudent({ ...editableStudent, special_needs_notes: e.target.value })} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Health Info</p>
                  {!isEditingStudent ? (
                    <p className="text-sm text-slate-700 whitespace-pre-line">{studentDetail.health_info || '—'}</p>
                  ) : (
                    <textarea className="w-full border border-slate-200 rounded px-2 py-1" rows={3} value={editableStudent?.health_info || ''} onChange={(e) => setEditableStudent({ ...editableStudent, health_info: e.target.value })} />
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Package</p>
                    {!isEditingStudent ? (
                      <p className="text-sm text-slate-700">{studentDetail.package_name || '—'}</p>
                    ) : (
                      <input className="w-full border border-slate-200 rounded px-2 py-1" value={editableStudent?.package_name || ''} onChange={(e) => setEditableStudent({ ...editableStudent, package_name: e.target.value })} />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Attendance Days / Month</p>
                    {!isEditingStudent ? (
                      <p className="text-sm text-slate-700">{studentDetail.monthly_attendance_days ?? '—'}</p>
                    ) : (
                      <input type="number" className="w-full border border-slate-200 rounded px-2 py-1" min="1" max="31" value={editableStudent?.monthly_attendance_days ?? ''} onChange={(e) => setEditableStudent({ ...editableStudent, monthly_attendance_days: e.target.value === '' ? null : parseInt(e.target.value, 10) })} />
                    )}
                  </div>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Student Attendance</p>
                    <p className="text-sm text-slate-700">View time in / time out for this student in Malaysia local time</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-slate-500">Month</label>
                    <Input type="month" value={attendanceMonth} onChange={(e) => setAttendanceMonth(e.target.value)} className="w-40" data-testid="student-attendance-month-input" />
                  </div>
                </div>
                {attendanceLoading ? (
                  <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
                ) : studentAttendance.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">No attendance records for this month</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left border-collapse">
                      <thead>
                        <tr>
                          <th className="border-b border-slate-200 px-3 py-2 text-xs uppercase text-slate-500">Date</th>
                          <th className="border-b border-slate-200 px-3 py-2 text-xs uppercase text-slate-500">Status</th>
                          <th className="border-b border-slate-200 px-3 py-2 text-xs uppercase text-slate-500">Time In</th>
                          <th className="border-b border-slate-200 px-3 py-2 text-xs uppercase text-slate-500">Time Out</th>
                          <th className="border-b border-slate-200 px-3 py-2 text-xs uppercase text-slate-500">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentAttendance.map((item) => (
                          <tr key={item.id} className="odd:bg-slate-50">
                            <td className="border-b border-slate-200 px-3 py-2 text-sm text-slate-700">{item.date}</td>
                            <td className="border-b border-slate-200 px-3 py-2 text-sm text-slate-700">{item.status}</td>
                            <td className="border-b border-slate-200 px-3 py-2 text-sm text-slate-700">{formatMalaysiaTimeOnly(item.time_in)}</td>
                            <td className="border-b border-slate-200 px-3 py-2 text-sm text-slate-700">{formatMalaysiaTimeOnly(item.time_out)}</td>
                            <td className="border-b border-slate-200 px-3 py-2 text-sm text-slate-700">{item.note || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => {
                    if (studentDetail.parent_user_id) {
                      setParentProfileUserId(studentDetail.parent_user_id);
                      setParentProfileOpen(true);
                    } else {
                      toast('No parent account linked');
                    }
                  }} data-testid="open-parent-profile-button">Open Parent Profile</Button>
                  <Button variant="outline" onClick={() => {
                    if (isEditingStudent) {
                      setIsEditingStudent(false);
                      setEditableStudent(null);
                    } else {
                      setIsEditingStudent(true);
                      setEditableStudent(studentDetail ? { ...studentDetail } : null);
                    }
                  }} data-testid="toggle-edit-student-button">{isEditingStudent ? 'Cancel Edit' : 'Edit Student'}</Button>
                  {isEditingStudent && (
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                      try {
                        const payload = { ...editableStudent };
                        delete payload.id;
                        delete payload.created_at;
                        delete payload._id;
                        await axios.put(`${API}/api/students/${studentDetail.id}`, payload, { withCredentials: true });
                        toast.success('Student updated');
                        setIsEditingStudent(false);
                        setStudentDetail({ ...studentDetail, ...editableStudent });
                        setEditableStudent(null);
                        fetchAll();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to save student');
                      }
                    }} data-testid="save-student-button">Save Changes</Button>
                  )}
                </div>
                <div>
                  <Button variant="outline" onClick={() => { setIsEditingStudent(false); setEditableStudent(null); setStudentDetail(null); }}>Close</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Parent Profile Dialog (inline) */}
      <Dialog open={parentProfileOpen} onOpenChange={(o) => !o && setParentProfileOpen(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="parent-profile-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Parent Profile</DialogTitle>
            <DialogDescription>View or edit the parent account linked to this student</DialogDescription>
          </DialogHeader>
          {parentProfileUserId && (
            <ProfileForm
              userId={parentProfileUserId}
              viewOnly={false}
              onSaved={() => {
                fetchAll();
                setParentProfileOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Report View */}
      <Dialog open={!!reportView} onOpenChange={(o) => !o && setReportView(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-report-view-dialog">
          <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                {reportView?.type === 'ot' ? 'OT Report' : 'Report'} — {reportView?.student_name} {reportView?.type === 'ot' ? '' : `— ${reportView?.month}`}
              </DialogTitle>
              <DialogDescription>
                By {reportView?.teacher_name} • {reportView?.type === 'ot' ? '3-month OT progress report' : `Rating: ${reportView?.overall_rating || '—'}/5`}
                {attendanceSummary && (
                  <span className="ml-4 text-xs text-slate-500">• Attendance: {attendanceSummary.days_present} days, {attendanceSummary.late_count} late, {attendanceSummary.overtime_count} OT</span>
                )}
              </DialogDescription>
            </DialogHeader>
          {reportView && (
            <div className="space-y-4">
              {[
                  { label: "Highlights / Teacher's Feedback", key: 'highlights' },
                  { label: 'Special Occasion / Event / Activity of the Month', key: 'special_event' },
                  { label: 'Behaviour Observation', key: 'therapy_notes' },
                  { label: 'Areas Needing Improvement', key: 'areas_of_improvement' },
                  { label: 'Recommendations for Parents', key: 'recommendations' },
              ].filter((f) => reportView[f.key]).map((f) => (
                <div key={f.key}>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">{f.label}</p>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{reportView[f.key]}</p>
                </div>
              ))}
              <div className="border-t pt-3">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Pyramid of Learning</p>
                <p className="text-xs text-slate-500 mb-2">Overall rating is calculated from the average of the POL ratings.</p>
                <div className="text-xs text-slate-500 mb-2">
                  <p><strong>Level 1</strong> Emerging – Requires extensive support</p>
                  <p><strong>Level 2</strong> Developing – Requires frequent support</p>
                  <p><strong>Level 3</strong> Progressing – Demonstrates skill with some support</p>
                  <p><strong>Level 4</strong> Proficient – Demonstrates skill independently</p>
                  <p><strong>Level 5</strong> Advanced – Consistently demonstrates skill beyond expectations</p>
                </div>
                <CnsPyramid ratings={reportView.cns_ratings || {}} editable={false} />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => window.open(`${API}/${reportView?.type === 'ot' ? 'api/ot-reports' : 'api/reports'}/${reportView.id}/pdf`, '_blank')}
                  data-testid="admin-download-report-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />Download PDF
                </Button>
                <Button className="bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={() => { approveReport(reportView.id, 'rejected'); setReportView(null); }}>Reject</Button>
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white" onClick={() => { approveReport(reportView.id, 'approved'); setReportView(null); }}>Approve</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStudentsSection;
